import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {OrderService} from "../../services/OrderService";
import {FeatureNames} from "../../../types/UserConfig";
import {BehaviorSubject, Observable, Scheduler, Subject} from "rxjs";
import {placedOrdersAddChange, PlacedOrdersChange, placedOrdersRefreshChange} from "./internal/PlacedOrdersChange";
import {
    placedBrokerOrder,
    placedBrokerOrders,
    PlacedOrders,
    placedSimulatedOrder,
    placedSimulatedOrders
} from "./models/PlacedOrder";
import {applyChange} from "./internal/PlacedOrderStoreChangeUtil";
import {OrderNumber} from "../../models/OrderRecord";
import {DateTimeUtil, MARKET_ZONE, marketTime} from "../../../js/util/DateTimeUtil";
import moment from "moment";

export class PlacedOrdersStore {

    public static readonly id = "placedOrdersStore";

    private readonly changes$ = new Subject<PlacedOrdersChange>();
    private readonly current$ = new BehaviorSubject<PlacedOrders>([]);

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly orderService: OrderService) {
        if (currentUserService.hasFeature(FeatureNames.SCREEN_EQUITY_ORDERS, FeatureNames.SCREEN_OPTION_ORDERS)) {
            this.init();
        }
    }

    private init() {
        this.buildCurrent();
        this.refreshCurrent();
    }

    private buildCurrent() {
        Observable.of(true)
                  .switchMap(() => this.baseline())
                  .flatMap((baseline) => this.watchChanges(baseline))
                  .subscribe((current) => this.current$.next(current));
    }

    private watchChanges(baseline: PlacedOrders): Observable<PlacedOrders> {
        return this.changes$
                   .scan((acc, next) => applyChange(acc, next), baseline)
                   .startWith(baseline);
    }

    private refreshCurrent() {
        const currentMarketTime = marketTime();
        const refreshTime = moment().tz(MARKET_ZONE).hour(17).minute(0).second(0);
        const timeUntilFirstRefresh = refreshTime.unix() - currentMarketTime.unix();

        Observable.timer(timeUntilFirstRefresh, DateTimeUtil.ONE_DAY_MILLIS)
                  .switchMap(() => this.baseline())
                  .map((baseline) => placedOrdersRefreshChange(baseline))
                  .subscribe((change) => this.changes$.next(change));
    }

    private baseline(): Observable<PlacedOrders> {
        const broker$ = Observable.fromPromise(this.orderService.openPlacedBrokerOrders())
                                  .catch(() => Observable.of([]))
                                  .map(placedBrokerOrders);
        const simulated$ = Observable.fromPromise(this.orderService.openPlacedSimulatedOrders())
                                     .catch(() => Observable.of([]))
                                     .map(placedSimulatedOrders);
        return Observable.forkJoin(broker$, simulated$).map(([a, b]) => [...a, ...b]);
    }

    public brokerOrderPlaced(orderNumber: OrderNumber) {
        const order = placedBrokerOrder(orderNumber);
        const change = placedOrdersAddChange(order);
        this.changes$.next(change);
    }

    public simulatedOrderPlaced(id: number) {
        const order = placedSimulatedOrder(id);
        const change = placedOrdersAddChange(order);
        this.changes$.next(change);
    }

    public get placed(): Observable<PlacedOrders> {
        return this.current$.observeOn(Scheduler.async);
    }
}
